import {
  canPlaceMatchWithoutConflict,
  canSwapMatchesWithoutConflict,
  type PlacementConflictIndex,
} from '../../../../../features/schedule/schedulePlacementConflict';
import {
  canPlacePendingMatchAtScheduledSlot,
  canSwapTwoScheduledMatches,
} from '../../../../../features/schedule/scheduleOverlap';
import type { Class } from '../../../../../types/tournament';
import type {
  IMatchScheduleItem,
  IScheduleConstraints,
  ITournamentSchedule,
} from '../../../../../types/schedule';

export type SwapModifierPreviewDims = {
  dimInvalidSwapTarget: boolean;
  ctrlSwapSubtleDim: boolean;
};

const INACTIVE_SWAP_PREVIEW_DIMS: SwapModifierPreviewDims = {
  dimInvalidSwapTarget: false,
  ctrlSwapSubtleDim: false,
};

export type SwapTargetPreviewParams = {
  activePlacementMatch: IMatchScheduleItem | null;
  scheduleConstraints: IScheduleConstraints | null;
  rangeItem: IMatchScheduleItem;
  allScheduledMatchesFlat: IMatchScheduleItem[];
  pendingPlacementSource: 'unscheduled' | 'scheduled_grid' | null;
  scheduleDragActive: boolean;
  dragPlacementSource: 'unscheduled' | 'scheduled' | null;
  blockInvalidPlacement?: boolean;
  canonicalSchedule?: ITournamentSchedule | null;
  scheduleTimeZone?: string;
  classIdToObject?: Record<string, Class>;
  swapConflictIndex?: PlacementConflictIndex | null;
};

function computeSwapTargetInvalid(params: SwapTargetPreviewParams): boolean {
  const {
    activePlacementMatch,
    scheduleConstraints,
    rangeItem,
    allScheduledMatchesFlat,
    pendingPlacementSource,
    scheduleDragActive,
    dragPlacementSource,
    blockInvalidPlacement,
    canonicalSchedule,
    scheduleTimeZone,
    classIdToObject,
    swapConflictIndex,
  } = params;

  if (!activePlacementMatch || !scheduleConstraints) {
    return false;
  }

  if (rangeItem.id === activePlacementMatch.id) {
    return false;
  }

  const sourceIsScheduledPairSwap =
    pendingPlacementSource === 'scheduled_grid' ||
    (scheduleDragActive && dragPlacementSource === 'scheduled');

  let dimInvalidSwapTarget = sourceIsScheduledPairSwap
    ? !canSwapTwoScheduledMatches(
        activePlacementMatch,
        rangeItem,
        allScheduledMatchesFlat,
        scheduleConstraints
      )
    : !canPlacePendingMatchAtScheduledSlot(
        activePlacementMatch,
        rangeItem,
        allScheduledMatchesFlat,
        scheduleConstraints
      );

  if (
    blockInvalidPlacement &&
    !dimInvalidSwapTarget &&
    canonicalSchedule &&
    scheduleTimeZone &&
    classIdToObject
  ) {
    const isUnscheduledSwap =
      pendingPlacementSource === 'unscheduled' ||
      (scheduleDragActive && dragPlacementSource === 'unscheduled');

    if (isUnscheduledSwap && rangeItem.scheduleDate && rangeItem.court) {
      const targetStartMs = new Date(rangeItem.scheduleDate).getTime();
      dimInvalidSwapTarget = !canPlaceMatchWithoutConflict({
        pending: activePlacementMatch,
        courtId: rangeItem.court.id,
        startMs: targetStartMs,
        schedule: canonicalSchedule,
        constraints: scheduleConstraints,
        timeZone: scheduleTimeZone,
        classIdToObject,
        excludeMatchIds: [activePlacementMatch.id, rangeItem.id],
        conflictIndex: swapConflictIndex,
      });
    } else {
      dimInvalidSwapTarget = !canSwapMatchesWithoutConflict(
        activePlacementMatch,
        rangeItem,
        canonicalSchedule,
        scheduleConstraints,
        scheduleTimeZone,
        classIdToObject,
        swapConflictIndex
      );
    }
  }

  return dimInvalidSwapTarget;
}

/** True when swap would be blocked (overlap or conflict when block mode ON). */
export function isSwapTargetBlockedByConflict(params: SwapTargetPreviewParams): boolean {
  return computeSwapTargetInvalid(params);
}

export type BuildSwapModifierPreviewDimsMapParams = {
  swapPreviewActive: boolean;
  swapModifierHeld?: boolean;
  activePlacementMatch: IMatchScheduleItem | null;
  scheduleConstraints: IScheduleConstraints | null;
  scheduledMatches: IMatchScheduleItem[];
  allScheduledMatchesFlat: IMatchScheduleItem[];
  pendingPlacementSource: 'unscheduled' | 'scheduled_grid' | null;
  scheduleDragActive: boolean;
  dragPlacementSource: 'unscheduled' | 'scheduled' | null;
  blockInvalidPlacement?: boolean;
  canonicalSchedule?: ITournamentSchedule | null;
  scheduleTimeZone?: string;
  classIdToObject?: Record<string, Class>;
  swapConflictIndex?: PlacementConflictIndex | null;
};

/** Swap-target preview: invalid targets vs subtle dim on valid non-source matches (Ctrl/Cmd only). */
export function getSwapModifierPreviewDims(params: {
  swapPreviewActive: boolean;
  swapModifierHeld?: boolean;
  activePlacementMatch: IMatchScheduleItem | null;
  scheduleConstraints: IScheduleConstraints | null;
  rangeItem: IMatchScheduleItem;
  allScheduledMatchesFlat: IMatchScheduleItem[];
  pendingPlacementSource: 'unscheduled' | 'scheduled_grid' | null;
  scheduleDragActive: boolean;
  dragPlacementSource: 'unscheduled' | 'scheduled' | null;
  blockInvalidPlacement?: boolean;
  canonicalSchedule?: ITournamentSchedule | null;
  scheduleTimeZone?: string;
  classIdToObject?: Record<string, Class>;
  swapConflictIndex?: PlacementConflictIndex | null;
}): SwapModifierPreviewDims {
  const {
    swapPreviewActive,
    swapModifierHeld,
    activePlacementMatch,
    scheduleConstraints,
    rangeItem,
    allScheduledMatchesFlat,
    pendingPlacementSource,
    scheduleDragActive,
    dragPlacementSource,
    blockInvalidPlacement,
    canonicalSchedule,
    scheduleTimeZone,
    classIdToObject,
    swapConflictIndex,
  } = params;

  if (!swapPreviewActive || !activePlacementMatch || !scheduleConstraints) {
    return INACTIVE_SWAP_PREVIEW_DIMS;
  }

  const dimInvalidSwapTarget = computeSwapTargetInvalid({
    activePlacementMatch,
    scheduleConstraints,
    rangeItem,
    allScheduledMatchesFlat,
    pendingPlacementSource,
    scheduleDragActive,
    dragPlacementSource,
    blockInvalidPlacement,
    canonicalSchedule,
    scheduleTimeZone,
    classIdToObject,
    swapConflictIndex,
  });

  const ctrlSwapSubtleDim =
    !!swapModifierHeld &&
    !dimInvalidSwapTarget &&
    rangeItem.id !== activePlacementMatch.id;

  return { dimInvalidSwapTarget, ctrlSwapSubtleDim };
}

/** Precompute swap-preview dim state once per render instead of per visible MatchItem. */
export function buildSwapModifierPreviewDimsMap(
  params: BuildSwapModifierPreviewDimsMapParams
): Map<number, SwapModifierPreviewDims> {
  const {
    swapPreviewActive,
    swapModifierHeld,
    activePlacementMatch,
    scheduleConstraints,
    scheduledMatches,
    allScheduledMatchesFlat,
    pendingPlacementSource,
    scheduleDragActive,
    dragPlacementSource,
    blockInvalidPlacement,
    canonicalSchedule,
    scheduleTimeZone,
    classIdToObject,
    swapConflictIndex,
  } = params;

  const map = new Map<number, SwapModifierPreviewDims>();
  if (!swapPreviewActive || !activePlacementMatch || !scheduleConstraints) {
    return map;
  }

  for (const match of scheduledMatches) {
    map.set(
      match.id,
      getSwapModifierPreviewDims({
        swapPreviewActive,
        swapModifierHeld,
        activePlacementMatch,
        scheduleConstraints,
        rangeItem: match,
        allScheduledMatchesFlat,
        pendingPlacementSource,
        scheduleDragActive,
        dragPlacementSource,
        blockInvalidPlacement,
        canonicalSchedule,
        scheduleTimeZone,
        classIdToObject,
        swapConflictIndex,
      })
    );
  }

  return map;
}
